import React, { Dispatch, SetStateAction, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import OutsideClickHandler from "react-outside-click-handler"
import { useConfig } from "configuration/useConfig"
import { useSelector } from "react-redux"
import { selectUserGroups } from "lib/store/services/auth/selectors"
import { sidebarConfig } from "navigation"
import { filterSidebarItems } from "navigation/utils"

import {
  BrandTitle,
  CollapseButton,
  Container,
  HoverTooltip,
  IconWrapper,
  InlineSectionTitle,
  InlineSubMenu,
  InlineSubNavExternalLink,
  InlineSubNavLink,
  Logo,
  Navigation,
  NavItem,
  NavLink,
  NavMainText,
  NavNoLink,
  NavSubText,
  NavTitle,
  SidebarClickOverlay,
  SidebarContent,
  SidebarHeader,
  Test,
  Title,
  TooltipContent,
  TooltipSubtitle,
  TooltipTitle,
  User,
  Wrapper
} from "./Sidebar.styled"

import { ReactComponent as UserIcon } from "images/icons/user-large.svg"
import { ReactComponent as ExternalIcon } from "images/icons/arrow-up-right-from-square.svg"
import { ReactComponent as ChevronLeftIcon } from "images/icons/chevron-left.svg"

type Props = {
  sideBarExpanded: boolean
  setSidebarExpanded: Dispatch<SetStateAction<boolean>>
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

export const Sidebar = ({
  sideBarExpanded,
  setSidebarExpanded,
  collapsed,
  setCollapsed
}: Props) => {
  const [expandedNav, setExpandedNav] = useState("")
  const [tempExpanded, setTempExpanded] = useState(false)
  const [hoveredItem, setHoveredItem] = useState<{
    title: string
    description: string
    top: number
  } | null>(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isTestEnvironment, config } = useConfig()
  const userGroups = useSelector(selectUserGroups)

  const getIconColor = () => {
    return "#4fd1c7"
  }

  // Filter sidebar items based on user groups and configuration
  const filteredItems = userGroups ? filterSidebarItems(sidebarConfig, userGroups, config) : []

  const sortedItems = filteredItems.sort((a, b) =>
    a.sortOrder && b.sortOrder ? a.sortOrder - b.sortOrder : 0
  )

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setExpandedNav("")
        if (collapsed && tempExpanded) {
          setTempExpanded(false)
        }
      }}
    >
      <Wrapper $expanded={sideBarExpanded}>
        <Container $collapsed={collapsed && !tempExpanded}>
          <SidebarClickOverlay onClick={() => setCollapsed(!collapsed)} />
          <SidebarContent $collapsed={collapsed && !tempExpanded}>
            <SidebarHeader $collapsed={collapsed && !tempExpanded}>
              <Title>
                <Logo onClick={() => navigate("/")} $isTestEnvironment={isTestEnvironment} />
                <BrandTitle $collapsed={collapsed && !tempExpanded}>Merchant Portal</BrandTitle>
              </Title>
            </SidebarHeader>
            {isTestEnvironment && <Test>test</Test>}
            <Navigation $collapsed={collapsed && !tempExpanded}>
              {sortedItems.map((item) => {
                const children = item.children || []
                const activeChild = children?.find(
                  (c) => c.routePath && pathname?.includes(c.routePath)
                )
                if (!item.title) return null
                return (
                  <React.Fragment key={item.id}>
                    <NavItem
                      $active={
                        pathname === item.routePath ||
                        (item.routePath !== "/" &&
                          item.routePath &&
                          pathname?.includes(item.routePath) &&
                          children?.length === 0) ||
                        !!activeChild
                      }
                    >
                      {children && children?.length !== 0 ? (
                        <NavNoLink
                          onClick={(e) => {
                            e.stopPropagation()
                            if (collapsed) {
                              if (tempExpanded && expandedNav === item.routePath) {
                                // If already expanded and showing this menu, collapse it
                                setTempExpanded(false)
                                setExpandedNav("")
                              } else {
                                // Expand and show this menu
                                setTempExpanded(true)
                                setExpandedNav(item?.routePath ?? "")
                                // Clear any hover tooltip when expanding
                                setHoveredItem(null)
                              }
                            } else {
                              expandedNav === item.routePath
                                ? setExpandedNav("")
                                : setExpandedNav(item?.routePath ?? "")
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (collapsed && !tempExpanded) {
                              const rect = e.currentTarget.getBoundingClientRect()
                              setHoveredItem({
                                title: item.title ?? "",
                                description: item.description ?? "",
                                top: rect.top
                              })
                            }
                          }}
                          onMouseLeave={() => {
                            if (collapsed && !tempExpanded) {
                              setHoveredItem(null)
                            }
                          }}
                          $subMenuExpanded={expandedNav === item.routePath}
                          $collapsed={collapsed && !tempExpanded}
                        >
                          {item.icon && (
                            <IconWrapper $color={getIconColor()}>
                              <item.icon />
                            </IconWrapper>
                          )}
                          <NavTitle $collapsed={collapsed && !tempExpanded}>
                            <NavMainText>{item.title}</NavMainText>
                            <NavSubText>{item.description}</NavSubText>
                          </NavTitle>
                        </NavNoLink>
                      ) : (
                        <NavLink
                          to={item?.routePath ?? ""}
                          onClick={(e) => {
                            e.stopPropagation()
                            setExpandedNav("")
                            setSidebarExpanded(false)
                          }}
                          onMouseEnter={(e) => {
                            if (collapsed && !tempExpanded) {
                              const rect = e.currentTarget.getBoundingClientRect()
                              setHoveredItem({
                                title: item.title ?? "",
                                description: item.description ?? "",
                                top: rect.top
                              })
                            }
                          }}
                          onMouseLeave={() => {
                            if (collapsed && !tempExpanded) {
                              setHoveredItem(null)
                            }
                          }}
                          $collapsed={collapsed && !tempExpanded}
                        >
                          {item.icon && (
                            <IconWrapper $color={getIconColor()}>
                              <item.icon />
                            </IconWrapper>
                          )}
                          <NavTitle $collapsed={collapsed && !tempExpanded}>
                            <NavMainText>{item.title}</NavMainText>
                            <NavSubText>{item.description}</NavSubText>
                          </NavTitle>
                        </NavLink>
                      )}
                    </NavItem>

                    {/* Inline submenu items */}
                    {children &&
                      children?.length !== 0 &&
                      expandedNav === item.routePath &&
                      !(collapsed && !tempExpanded) && (
                        <InlineSubMenu>
                          {children.map((sublink) => (
                            <div key={sublink.id}>
                              {sublink.section !== undefined && (
                                <InlineSectionTitle>{sublink.section}</InlineSectionTitle>
                              )}
                              {sublink.href ? (
                                <InlineSubNavExternalLink
                                  key={sublink.id}
                                  href={sublink.href}
                                  target={sublink.target || "_blank"}
                                  rel="noopener noreferrer"
                                >
                                  {sublink.title} <ExternalIcon />
                                </InlineSubNavExternalLink>
                              ) : (
                                <InlineSubNavLink
                                  key={sublink.id}
                                  to={sublink.routePath || ""}
                                  onClick={() => {
                                    setExpandedNav("")
                                    setSidebarExpanded(false)
                                    if (collapsed && tempExpanded) {
                                      setTempExpanded(false)
                                    }
                                  }}
                                  $active={pathname === sublink.routePath ? true : undefined}
                                >
                                  {sublink.title}
                                </InlineSubNavLink>
                              )}
                            </div>
                          ))}
                        </InlineSubMenu>
                      )}
                  </React.Fragment>
                )
              })}
            </Navigation>
            <hr />
            <User
              $collapsed={collapsed && !tempExpanded}
              $active={pathname === "/user"}
              to="/user"
              onClick={(e) => {
                e.stopPropagation()
                setSidebarExpanded(false)
              }}
              onMouseEnter={(e) => {
                if (collapsed && !tempExpanded) {
                  const rect = e.currentTarget.getBoundingClientRect()
                  setHoveredItem({
                    title: "User",
                    description: "User details",
                    top: rect.top
                  })
                }
              }}
              onMouseLeave={() => {
                if (collapsed && !tempExpanded) {
                  setHoveredItem(null)
                }
              }}
            >
              <IconWrapper $color={getIconColor()}>
                <UserIcon />
              </IconWrapper>
              <NavTitle $collapsed={collapsed && !tempExpanded}>
                <NavMainText>User</NavMainText>
                <NavSubText>User details</NavSubText>
              </NavTitle>
            </User>
            <CollapseButton
              $collapsed={collapsed && !tempExpanded}
              onClick={(e) => {
                e.stopPropagation()
                setCollapsed(tempExpanded ? true : !collapsed)
                setTempExpanded(false)
                setExpandedNav("")
              }}
            >
              <ChevronLeftIcon />
            </CollapseButton>
          </SidebarContent>
        </Container>

        {/* Hover Tooltip */}
        {hoveredItem && (
          <HoverTooltip $visible={!!hoveredItem} $top={hoveredItem.top}>
            <TooltipContent $visible={!!hoveredItem}>
              <TooltipTitle>{hoveredItem.title}</TooltipTitle>
              <TooltipSubtitle>{hoveredItem.description}</TooltipSubtitle>
            </TooltipContent>
          </HoverTooltip>
        )}
      </Wrapper>
    </OutsideClickHandler>
  )
}
